import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, lastValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { IApiResponse } from '../utils/common.interface';
import { AuthResponse, LoginRequest, User, WalletAccount } from '../models/auth.models';
import { CookieUtils } from '../Helper/cookie-utils';
import { getActiveCommunityId } from 'src/app/core/services/co-brand.service';

interface VerifyEmailPayload {
    email: string;
    otp?: string;
    token?: string;
}

interface ResetPasswordPayload {
    password: string;
}


@Injectable({
    providedIn: 'root'
})
export class AccountService {
    private readonly manualLogoutSuppressMs = 5000;

    // Shared short-lived cache for KYC status so the same lookup isn't fired by
    // every component on a page. Call clearKycStatusCache() after the user
    // completes/updates KYC so the next read reflects the new status immediately.
    private readonly kycStatusCacheTtlMs = 60_000;
    private kycStatusCache?: { ts: number; value: Promise<any> };
    public loaderStatus = new BehaviorSubject(true);
    account = new BehaviorSubject<WalletAccount>({
        walletAddress: '',
        chainId: '',
        networkId: '',
        walletProvider: '',
        init: true
    });
    accountObserve: Observable<WalletAccount>;
    private connectWalletModal: BehaviorSubject<boolean>;
    public connectObservable!: Observable<boolean>;
    private authenticated = new BehaviorSubject<boolean>(true);
    public authenticationObservable!: Observable<boolean>;
    private manualLogoutStartedAt: number | null = null;

    constructor(private http: HttpClient) {
        // Account
        this.accountObserve = this.account.asObservable();
        this.loaderStatus.asObservable();
        // Network connection
        this.connectWalletModal = new BehaviorSubject(false);
        this.connectObservable = this.connectWalletModal.asObservable();

        this.authenticationObservable = this.authenticated.asObservable();
    }

    /**
     * Set Account
     * @param {object} data
     */
    public setAccount(data: {
        walletAddress: string,
        chainId: string,
        networkId: number | string,
        walletProvider: string,
        init: boolean
    }) {
        this.account.next(data);
    }

    /**
     * Enables meta mask connection
     * @param data
     */
    public enableMetaMaskConnection(data: boolean) {
        this.connectWalletModal.next(data);
    }

    /**
     * Updates the authentication state. This method is idempotent,
     * meaning if the `data` parameter is the same as the current
     * authentication state, it will not trigger another state
     * update.
     *
     * @param data - The new authentication state.
     */
    public updateAuthentication(data: boolean) {
        if (data) {
            this.clearManualLogoutMarker();
        }

        const currentAuthState = this.authenticated.value;
        // Prevent multiple logout triggers
        if (currentAuthState === data) {
            return;
        }
        this.authenticated.next(data);
    }

    public markManualLogout(): void {
        this.manualLogoutStartedAt = Date.now();
    }

    public clearManualLogoutMarker(): void {
        this.manualLogoutStartedAt = null;
    }

    public shouldTreatUnauthorizedAsSessionExpiry(error: any): boolean {
        const statusCode = error?.status ?? error?.error?.status_code ?? error?.error?.status;
        return statusCode === 401 && !this.hasRecentManualLogout();
    }

    private hasRecentManualLogout(): boolean {
        if (this.manualLogoutStartedAt === null) {
            return false;
        }

        if ((Date.now() - this.manualLogoutStartedAt) > this.manualLogoutSuppressMs) {
            this.manualLogoutStartedAt = null;
            return false;
        }

        return true;
    }

    /**
     * Adds the community number when signing up on a community page (?community_id=), so the new
     * account joins that community. The backend sets it only when the account is created, so a
     * sign-in through the same request changes nothing.
     */
    private withSignupCommunity<T extends object>(payload: T): T | (T & { community_id: number }) {
        const communityId = getActiveCommunityId();
        return communityId ? { ...payload, community_id: communityId } : payload;
    }

    /**
     * Step 1 of wallet auth: Request a challenge nonce from the backend.
     * @param walletAddress The wallet address requesting authentication
     * @returns Observable with { nonce, message } to be signed by the wallet
     */
    public walletChallenge(walletAddress: string): Observable<{ status: boolean; data: { nonce: string; message: string } }> {
        return this.http.post<{ status: boolean; data: { nonce: string; message: string } }>(
            `${environment.API_BASE_URL_V3}/auth/wallet/challenge`,
            { wallet_address: walletAddress }
        );
    }

    /**
     * Step 2 of wallet auth: Send signed challenge back for verification.
     * Backend recovers the signer address and verifies it matches the claimed wallet.
     * @param params wallet_address, signature, nonce, last_login_os, last_login_browser
     * @returns AuthResponse with user data + session cookie
     */
    public walletVerify(params: {
        wallet_address: string;
        signature: string;
        nonce: string;
        last_login_os: string;
        last_login_browser: string;
        first_name?: string;
        last_name?: string;
        email?: string;
        is_policy_accepted?: boolean;
        user_register_type?: number;
        referral_key?: string;
    }): Observable<AuthResponse> {
        const referralKey = CookieUtils.getCookie('referral_key');
        const payload = this.withSignupCommunity(referralKey ? { ...params, referral_key: referralKey } : params);
        return this.http.post<AuthResponse>(
            `${environment.API_BASE_URL_V3}/auth/wallet/verify`,
            payload
        ).pipe(
            tap((response) => {
                if (response) {
                    CookieUtils.deleteCookie('referral_key');
                }
            })
        );
    }

    async getUserById(): Promise<IApiResponse<User>> {
        let userDetails = this.http.get<IApiResponse<User>>(`${environment.API_BASE_URL_V3}`);
        return await lastValueFrom(userDetails);
    }

    public getUserById$(): Observable<IApiResponse<User>> {
        return this.http.get<IApiResponse<User>>(`${environment.API_BASE_URL_V3}`);
    }

    public updateUser(params: Record<string, unknown>) {
        return this.http.patch(`${environment.API_BASE_URL_V3}/user`, params);
    }

    /**
     * Registers a regulated (custodial) user. Self-custody users go through
     * the wallet challenge/verify flow via `walletChallenge` + `walletVerify`.
     */
    public register(params: Record<string, unknown>): Observable<any> {
        const referralKey = CookieUtils.getCookie('referral_key');
        const payload = this.withSignupCommunity(referralKey ? { ...params, referral_key: referralKey } : params);
        return this.http.post(`${environment.API_BASE_URL_V3}/regulated-sign-up`, payload).pipe(
            tap((response) => {
                if (response) {
                    CookieUtils.deleteCookie('referral_key');
                }
            })
        );
    }

    /**
     * Logins account service
     * @param params
     * @returns
     */
    public login(params: LoginRequest): Observable<AuthResponse> {
        return this.http.post<AuthResponse>(environment.API_BASE_URL_V3 + '/regulated-sign-in', params);
    }

    /**
     * Sends email
     * @param userId
     * @returns
     */
    public sendEmail(userId: string) {
        return this.http.get(`${environment.API_BASE_URL}/user/send-mail?id=${userId}`);
    }

    /**
     * Verifys email
     * @param params
     * @returns
     */
    public verifyEmail(params: VerifyEmailPayload): Observable<IApiResponse<User>> {
        return this.http.post<IApiResponse<User>>(`${environment.API_BASE_URL}/user/verify-mail`, params);
    }

    /**
     * Updates email
     * @param params
     * @returns
     */
    public updateEmail(userId: string, params: Record<string, unknown>) {
        return this.http.patch(`${environment.API_BASE_URL}/user?id=${userId}`, params);
    }

    public forgotPassword(email: string) {
        return this.http.post(`${environment.API_BASE_URL_V3}/regulated/forget-password`, { email });
    }

    public resetPassword(token: string, params: ResetPasswordPayload) {
        return this.http.post(`${environment.API_BASE_URL_V3}/regulated/update-password`, { token, ...params });
    }

    public validateToken(token: string) {
        return this.http.post(`${environment.API_BASE_URL_V3}/regulated/validate-token`, { token });
    }

    /**
     * Loaders status update
     * @param {boolean} status
     */
    public loaderStatusUpdate(status: boolean) {
        this.loaderStatus.next(status);
    }

    /**
     * Googles auth
     * @param {any} params
     * @returns
     */
    public googleAuth(params: any): Observable<any> {
        const referralKey = CookieUtils.getCookie('referral_key');
        const payload = this.withSignupCommunity(referralKey ? { ...params, referral_key: referralKey } : params);
        return this.http.post(`${environment.API_BASE_URL_V3}/googleAuth`, payload).pipe(
            tap((response) => {
                if (response) {
                    CookieUtils.deleteCookie('referral_key');
                }
            })
        );
    }

    public getKycStatus() {
        const now = Date.now();
        if (this.kycStatusCache && now - this.kycStatusCache.ts < this.kycStatusCacheTtlMs) {
            return this.kycStatusCache.value;
        }
        const value = firstValueFrom(this.http.get(`${environment.API_BASE_URL_V3}/get-user-admin-kyc-status`));
        this.kycStatusCache = { ts: now, value };
        value.catch(() => {
            // Evict on failure so the next caller retries instead of caching the error.
            if (this.kycStatusCache?.value === value) {
                this.kycStatusCache = undefined;
            }
        });
        return value;
    }

    /** Invalidate the cached KYC status (call after the user completes/updates KYC). */
    public clearKycStatusCache(): void {
        this.kycStatusCache = undefined;
    }

    /**
     * confirm email verfication
     * @param {{id:string,token:string}} payload
     * @returns
     */
    public confirmEmail(payload: { id: string, token: string }): Observable<IApiResponse> {
        return this.http.patch<IApiResponse>(`${environment.API_BASE_URL_V3}/email-verification?id=${payload.id}&token=${payload.token}`, {});
    }

    /**
     * resend email verification mail
     * @param{string}email
     */
    resendVerificationEmail(email: string): Observable<IApiResponse> {
        return this.http.get<IApiResponse>(`${environment.API_BASE_URL_V3}/resend-email?email=${email}`);
    }

    /**
     * update self custody user's email and name
     */
    updateProfile(payload: { first_name: string, last_name: string, email: string, wallet_address: string }) {
        return this.http.patch<IApiResponse>(`${environment.API_BASE_URL_V3}/update-user-details?wallet_address=${payload.wallet_address}`, payload);
    }
}
